import { z } from 'zod';
import { softEnum, softEnumWith } from './soft-enum.js';
/**
 * 신청 자격 (docs/HERMES.md).
 *
 * **슬롯이 아니라 목록이다.** 「지역 하나, 업력 하나」가 아니라 조건이 여럿이다.
 * 고정 칸을 파면 조건들이 서로를 밀어낸다 — `name` 없이 지원 항목을 담으려다
 * 일곱이 둘로 접혔던 것과 같은 실수다.
 *
 * 실측(open 1,825건): 자격 절 1,383(76%) · 결격 844(46%) · 지역 605(33%) ·
 * 업종 586(32%) · 근로자 427(23%) · 업력 271(15%) · 매출 187(10%).
 * 그런데 우리가 화면에 보여주던 `grants.target` 은 65%가 「중소기업」이었다 —
 * 「내가 신청할 수 있나」에 아무 답을 못 한다.
 */
export const ELIGIBILITY_KINDS = [
    /** 「관내」「대구광역시 동구」「전국 소재」 */
    'region',
    /** 「창업 7년 이내」「업력 10년 이내」 */
    'business_age',
    /** 「연매출 100억원 이하」 */
    'revenue',
    /** 「상시근로자 10인 이상」 */
    'employees',
    /** 「제조업」「폐기물 수집·운반·처리업」 */
    'industry',
    /** 「벤처기업인증서 보유」「ISO 인증」 */
    'certification',
    /**
     * **결격 사유.** 「세금 체납 기업 제외」「기 지원받은 기업 제외」
     *
     * 자격과 반드시 갈라야 한다. 섞이면 「체납 기업 제외」가 「체납 기업 대상」
     * 으로 읽힌다 — 정반대다. 실측 846건(46%)이라 무시할 크기가 아니다.
     */
    'exclusion',
    'other',
];
/**
 * **시간 단위는 셋 다 실측으로 넣었다.**
 *
 * `months` — 업력을 「창업 18개월 이내」로 말하는 공고가 있는데 `years` 밖에
 * 없어서 파일럿 25건 중 한 건이 문서째 422 로 거부됐다.
 *
 * `days`·`hours` — 판정 기록에 사유를 남기기 시작하자마자 드러났다.
 * 「교육 40시간 이수」·「30일 이내 신청」이 담길 곳이 없었다.
 *
 * **반올림으로 때우지 않는다.** 18개월을 1년이나 2년으로 바꾸면 필터가 틀린
 * 답을 주고, 40시간을 2일로 바꾸면 조건 자체가 다른 말이 된다.
 *
 * **`other` 는 넣지 않는다.** 모델이 그 값을 보내려 했지만, 단위를 모르는
 * 숫자는 「무엇의 7인지」를 모르는 숫자다 — `unit_required` 가 막으려는 것이
 * 정확히 그것이다. 목록에 없으면 `valueNum` 을 비우고 문구만 남기면 된다.
 */
export const ELIGIBILITY_UNITS = [
    'years', 'months', 'days', 'hours', 'won', 'people', 'percent',
];
/** 「7년 **이내**」는 max, 「10인 **이상**」은 min. 이게 있어야 필터가 된다. */
export const ELIGIBILITY_BOUNDS = ['max', 'min', 'exact'];
/**
 * 조건 하나.
 *
 * **`valueText` 가 곧 인용문이다.** 지원 행처럼 `evidence` 를 따로 받지 않는다 —
 * 조건 문구 자체가 원문에서 잘라낸 것이라 근거를 또 받는 것은 같은 말을 두 번
 * 시키는 것이다. 원문에 없으면 버린다.
 */
export const eligibilityRowSchema = z.object({
    /**
     * 분류다. **모르는 값이 와도 항목을 버리지 않는다** — `other` 로 떨어진다.
     * 이 값이 틀려도 조건 문장은 그대로 쓸모가 있다.
     */
    kind: softEnumWith(ELIGIBILITY_KINDS, 'other'),
    /** 원문 그대로의 조건 문구. 「창업 7년 이내」 */
    /**
     * 400자까지. 200이던 때 실측에서 막혔다 — 결격사유는 한 문장에 여러 조항을
     * 「, 」로 이어 붙이는 게 보통이라(「휴·폐업, 부도, 법정관리, 파산·회생절차…」)
     * 200자를 넘는 것이 드물지 않다. 잘라 오게 하면 원문 대조에 실패한다.
     */
    valueText: z.string().min(1).max(400),
    /** 비교 가능한 숫자. **`valueText` 안에 있어야 한다.** */
    valueNum: z.number().int().min(0).nullable().default(null),
    /**
     * **모르면 비운다.** 「40시간」의 `hours` 를 몰랐다고 조건 문장까지 버렸던
     * 것이 사고였다. 단위가 비면 숫자도 같이 비고(§verifyEligibilityRow),
     * 조건은 남는다.
     */
    unit: softEnum(ELIGIBILITY_UNITS),
    bound: softEnum(ELIGIBILITY_BOUNDS),
    /**
     * 코드 (REFACTOR §3.2). `kind = certification` 이면 자격 14 중 하나, `kind = industry`
     * 이면 업종 코드 중 하나. 표에 없는 값은 서버가 비운다 — 문구(`valueText`)는 남는다.
     */
    code: z.string().max(40).nullable().default(null),
    confidence: z.number().int().min(0).max(100).default(0),
});
//# sourceMappingURL=grant-eligibility.js.map